/* eslint-disable max-lines */
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from 'react-router-dom';
import React, { Suspense, lazy, useEffect } from 'react';
import { getUserCompanies, getUserProfileData } from 'ducks/user/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  useFeatureFlags,
  useFeatureFlagsAnyEnabled,
  useFeatureFlagsLoaded,
  useShowAiBuilder,
  useShowLegacyProduction
} from '@hooks/useFeatureFlags';

import { AccountSuspendedPage } from '../_clean-code/pages/AccountSuspendedPage';
import { PermissionRoute } from '../_clean-code/components/PermissionRoute';
import AppLayout from '../layouts/AppLayout';
import { EMachineListsVariants } from '_clean-code/ducks/realtime/reducers';
import { ETvModeLayout } from '_clean-code/types/config';
import ErrorPage, { NoCompanyAccessPage } from '../_clean-code/pages/ErrorPage';
import { MachineUIConfigScreen } from '_clean-code/pages/UIConfigScreen/MachineUIConfigScreen';
import { NoMachineGroupsPage } from '../_clean-code/pages/NoMachineGroupsPage';
import Spinner from '@components/spinner';
import { SpinnerComponent } from '@components/index';
import { Translate } from '@utils/i18n';
import { cn } from '@utils/tailwind-class.merge';
import { configIsLoadingSelector } from 'ducks/config/selectors';
import { fetchSettingsForProduction } from '_clean-code/ducks/production/actions';
import { getCompanyUIConfig } from 'ducks/config/actions';
import { getMachines } from 'ducks/machines/actions';
import { getUserMachineGroupsSelector } from 'ducks/user/selectors';
import { loadMachinesSortingFilter } from '_clean-code/ducks/realtime/actions';
import styles from './mainLayoutRoute.module.scss';
import { useSentryContext } from '_clean-code/hooks/index';
import { useCanaryChannel } from '_clean-code/hooks/useCanaryChannel';
import { useUserCompanies } from '_clean-code/api/hooks/useUserCompanies';
import { useUserProfile } from '_clean-code/api/hooks/useUserProfile';
import { useProductionPermissions } from '_clean-code/hooks/useProductionPermissions';

const LazyHistorical = lazy(() => import('../containers/historical'));
const LazyMetrics = lazy(() => import('../_clean-code/pages/MetricsPage'));
const AIAnalytics = lazy(() => import('../_clean-code/pages/AIAnalytics'));

const LazyOperatorPlanningPage = lazy(
  () => import('../_clean-code/pages/OperatorPlanningPage')
);
const LazyProduction = lazy(
  () => import('../_clean-code/pages/ProductionPage')
);
const LazyBasicProduction = lazy(
  () => import('../_clean-code/pages/BasicProductionPage')
);

const LazyShiftsNotConsidered = lazy(
  () => import('../containers/shifts-not-considered')
);
const LazyAlertsSettingsV3 = lazy(
  () => import('../containers/settings/alerts-new')
);

const LazyAlertSettingsPageV2 = lazy(
  () => import('../_clean-code/pages/AlertSettingsPage')
);

const LazyStopSettingsV2 = lazy(
  () => import('../_clean-code/pages/StopSettingsV2')
);
const LazyTicket = lazy(() => import('../containers/ticket'));
const LazyUsersSettings = lazy(
  () => import('../_clean-code/pages/UsersSettings')
);
const LazyDataReports = lazy(() => import('../containers/data'));
const MainDashboardPage = lazy(() => import('../pages/mainDashboardPage'));
const LazyMachinesSetting = lazy(
  () => import('../containers/machine-settings')
);
const RealTimePage = lazy(() => import('../_clean-code/pages/RealTimePage'));
const RealTimePageV2 = lazy(
  () => import('../_clean-code/pages/RealTimePageV2')
);
const ShiftSettingsPage = lazy(
  () => import('../_clean-code/pages/ShiftSettingsPage')
);
const DataAnalysisPage = lazy(
  () => import('../_clean-code/pages/DataAnalysisPage')
);
const UIConfigScreen = lazy(
  () => import('../_clean-code/pages/UIConfigScreen')
);
const LazyProductionSettingsV2 = lazy(
  () => import('../_clean-code/pages/ProductionSettingsV2')
);

const TVRealTimePage = lazy(
  () => import('../_clean-code/pages/TVRealTimePage')
);
const TVOperatorViewPage = lazy(
  () => import('../_clean-code/pages/TVOperatorViewPage')
);
const TvModeRedirectPage = lazy(() => import('../pages/tv-mode-redirect'));

const Reports = lazy(() => import('../_clean-code/pages/Reports'));

const LazySettingsV2 = lazy(() => import('../_clean-code/pages/SettingsV2'));
const LazyRealtimeSettings = lazy(
  () => import('../_clean-code/pages/RealtimeSettings')
);
const LazyGoals = lazy(() => import('../_clean-code/pages/GoalsSettings'));
const LazyDeleteAccount = lazy(
  () => import('../_clean-code/pages/DeleteAccountPage')
);
const LazyDatabases = lazy(() => import('../_clean-code/pages/Databases'));
const LazyViewAlerts = lazy(() => import('../_clean-code/pages/ViewAlerts'));
const LazyBasicProductionImport = lazy(
  () =>
    import(
      '../_clean-code/pages/BasicProductionPage/components/BasicProductionImport'
    )
);

let didLoadCompanies = false;

function MainLayoutRoute() {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    data: companies,
    error: companiesError,
    isLoading: companiesLoading,
    selectedCompany
  } = useUserCompanies();
  const { userData, error: userProfileError } = useUserProfile({
    enabled: !!companies
  });
  const { canCreate } = useProductionPermissions();

  const isLegacyProductionEnabled = useShowLegacyProduction();
  const showAiBuilder = useShowAiBuilder();
  const areFeatureFlagsLoaded = useFeatureFlagsLoaded();

  const { reportSubscription, realtimeV2, operatorsV2, alertsV2 } =
    useFeatureFlags();
  const { availability, prodBasic, prodFull, prodLegacy } =
    useFeatureFlagsAnyEnabled();

  useSentryContext({ userData });

  useEffect(() => {
    if (!didLoadCompanies && companies) {
      dispatch(getCompanyUIConfig());
      dispatch(fetchSettingsForProduction());
      didLoadCompanies = true;
    }
  }, [companies, dispatch]);

  if (companiesError || userProfileError) {
    return <ErrorPage error={companiesError || userProfileError} />;
  }

  // Companies still loading → app loader, never a blank screen.
  if (companiesLoading || !companies) {
    return <Spinner id="main-loader" />;
  }

  // Authenticated but no company memberships → recoverable no-access state.
  if (companies.length === 0) {
    return <NoCompanyAccessPage />;
  }

  // Companies exist but the first allowed company hasn't been selected/persisted
  // yet (one render). useUserCompanies settles it; show the loader until then so
  // no TSOA route fires with a missing/stale x-company-id header.
  if (!selectedCompany) {
    return <Spinner id="main-loader" />;
  }

  return (
    <Routes>
      <Route
        path="dashboard"
        element={
          <WithMainLayout
            title={<Translate value="dashboard" />}
            render={() => <MainDashboardPage />}
          />
        }
      />

      {/*
        Declared unconditionally: if the route only existed while `showAiBuilder`
        is true, a reload on /ai-analytics would fall through to the catch-all and
        redirect to /realtime before the feature flags finish loading.
      */}
      <Route
        path="ai-analytics"
        element={
          <WithMainLayout
            title={<Translate value="ai_analytics" />}
            render={() =>
              showAiBuilder ? (
                <AIAnalytics />
              ) : areFeatureFlagsLoaded ? (
                GoToDefaultRoute
              ) : (
                <SpinnerComponent id="ai-analytics-loader" />
              )
            }
          />
        }
      />
      <Route
        path="ai-builder"
        element={<Navigate to="/ai-analytics" replace />}
      />

      <Route
        path="analytics/*"
        element={
          <WithMainLayout
            title={
              <Translate
                value={
                  realtimeV2 && location.pathname.includes('analytics/shift')
                    ? 'historical_shifts'
                    : 'analytics_sidebar'
                }
              />
            }
            className={styles.pareto}
            render={(params) => (
              <LazyHistorical {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="view-alerts"
        element={
          <WithMainLayout
            title={<Translate value="historical_alerts" />}
            render={() => (alertsV2 ? <LazyViewAlerts /> : GoToDefaultRoute)}
          />
        }
      />

      <Route
        path="realtime"
        element={
          <WithMainLayout
            title={<Translate value="machines_sidebar" />}
            render={() => (realtimeV2 ? <RealTimePageV2 /> : <RealTimePage />)}
          />
        }
      />
      <Route
        path="machinesv2"
        element={
          <WithMainLayout
            title={<Translate value="machines_sidebar" />}
            render={() => <RealTimePageV2 />}
          />
        }
      />

      <Route
        path="realtime/tv-mode"
        element={
          <WithMainLayout
            render={() => <TVRealTimePage displayMode={ETvModeLayout.Single} />}
          />
        }
      />
      <Route
        path="realtime/tv-mode2"
        element={
          <WithMainLayout
            render={() => (
              <TVRealTimePage displayMode={ETvModeLayout.Grid2x2} />
            )}
          />
        }
      />
      <Route
        path="realtime/tv-mode3"
        element={
          <WithMainLayout
            render={() => (
              <TVRealTimePage displayMode={ETvModeLayout.Grid3x3} />
            )}
          />
        }
      />
      <Route
        path="realtime/tv-mode2x6"
        element={
          <WithMainLayout
            render={() => (
              <TVRealTimePage displayMode={ETvModeLayout.Grid6x3} />
            )}
          />
        }
      />
      <Route
        path="realtime/tv-mode4x8"
        element={
          <WithMainLayout
            render={() => (
              <TVRealTimePage displayMode={ETvModeLayout.Grid4x8} />
            )}
          />
        }
      />
      <Route
        path="realtime/tv-mode-operators"
        element={<WithMainLayout render={() => <TVOperatorViewPage />} />}
      />
      <Route
        path="realtime/tv-mode-single-operator"
        element={
          <WithMainLayout
            render={() => <TVOperatorViewPage forceSingleOperator />}
          />
        }
      />
      <Route
        path="realtime/tv-mode-redirect"
        element={<WithMainLayout render={() => <TvModeRedirectPage />} />}
      />
      <Route
        path="realtime/:machineId/ui-configure"
        element={
          <WithMainLayout
            title={<Translate value="ui_config" />}
            render={({ machineId }) => (
              <MachineUIConfigScreen machineId={machineId} />
            )}
          />
        }
      />
      <Route
        path="reports/*"
        element={
          <WithMainLayout
            title={<Translate value="reports" />}
            render={() => (reportSubscription ? <Reports /> : GoToDefaultRoute)}
          />
        }
      />
      <Route
        path="databases/*"
        element={
          <WithMainLayout
            title={<Translate value="databases" />}
            render={() => <LazyDatabases />}
          />
        }
      />
      <Route
        path="production-import"
        element={
          <WithMainLayout
            title={<Translate value="import" />}
            render={() =>
              prodBasic || prodFull ? (
                canCreate ? (
                  <LazyBasicProductionImport />
                ) : (
                  <Navigate to="/databases/production" />
                )
              ) : (
                GoToDefaultRoute
              )
            }
          />
        }
      />
      <Route
        path="production/*"
        element={
          <WithMainLayout
            //TODO: add subtitle
            title={<Translate value="production_sidebar" />}
            render={(params) =>
              prodLegacy ? (
                <LazyProduction {...({ match: { params } } as any)} />
              ) : isLegacyProductionEnabled ? (
                <LazyBasicProduction {...({ match: { params } } as any)} />
              ) : (
                GoToDefaultRoute
              )
            }
          />
        }
      />
      <Route
        path="alerts/*"
        element={
          <PermissionRoute screen="alertSettings">
            <WithMainLayout
              title={<Translate value="alert_settings" />}
              className={styles.alerts}
              render={(params) =>
                availability ? (
                  alertsV2 ? (
                    <LazyAlertSettingsPageV2 />
                  ) : (
                    <LazyAlertsSettingsV3
                      {...({ match: { params } } as any)}
                      key={location.key}
                    />
                  )
                ) : (
                  GoToDefaultRoute
                )
              }
            />
          </PermissionRoute>
        }
      />
      <Route
        path="stopsettings/*"
        element={
          <PermissionRoute screen="stopSettings">
            <WithMainLayout
              title={<Translate value="stop_label_settings" />}
              headerTitleTextClassName="tw-text-xl tw-font-semibold tw-text-[#3b4140]"
              className={styles.stopLabels}
              render={(params) =>
                availability ? (
                  <LazyStopSettingsV2 {...({ match: { params } } as any)} />
                ) : (
                  GoToDefaultRoute
                )
              }
            />
          </PermissionRoute>
        }
      />
      <Route
        path="metrics"
        element={
          <WithMainLayout
            title={<Translate value="metrics" />}
            render={(params) =>
              availability ? (
                <LazyMetrics {...({ match: { params } } as any)} />
              ) : (
                GoToDefaultRoute
              )
            }
          />
        }
      />

      <Route
        path="operator-settings/:tab?/:action?"
        element={
          <PermissionRoute screen="operatorSettings">
            <WithMainLayout
              title={<Translate value="operator_settings" />}
              render={(params) =>
                availability && operatorsV2 ? (
                  <LazyOperatorPlanningPage
                    {...({ match: { params } } as any)}
                  />
                ) : (
                  GoToDefaultRoute
                )
              }
            />
          </PermissionRoute>
        }
      />
      <Route
        path="shift-not-considered"
        element={
          <PermissionRoute screen="shiftsNotConsidered">
            <WithMainLayout
              title={<Translate value="idleShiftsSettings" />}
              className={styles.idleShifts}
              render={(params) =>
                availability ? (
                  <LazyShiftsNotConsidered {...{ match: { params } }} />
                ) : (
                  GoToDefaultRoute
                )
              }
            />
          </PermissionRoute>
        }
      />
      <Route
        path="create-ticket"
        element={
          <WithMainLayout
            title={<Translate value="support_ticket" />}
            className={styles.tickets}
            render={(params) => <LazyTicket {...{ match: { params } }} />}
          />
        }
      />
      <Route
        path="machine-settings"
        element={
          <PermissionRoute screen="machineSettings">
            <WithMainLayout
              title={<Translate value="machine_settings" />}
              render={(params) => (
                <LazyMachinesSetting {...({ match: { params } } as any)} />
              )}
            />
          </PermissionRoute>
        }
      />
      <Route
        path="settings/goals/:tab?"
        element={
          <PermissionRoute screen="goals">
            <WithMainLayout
              title={<Translate value="goals" />}
              render={(params) => (
                <LazyGoals {...({ match: { params } } as any)} />
              )}
            />
          </PermissionRoute>
        }
      />
      <Route
        path="users-settings/:tab?"
        element={
          <PermissionRoute screen="usersSettings">
            <WithMainLayout
              title={<Translate value="users_settings" />}
              render={(params) => (
                <LazyUsersSettings {...({ match: { params } } as any)} />
              )}
            />
          </PermissionRoute>
        }
      />
      <Route
        path="delete-account"
        element={
          <WithMainLayout
            title={<Translate value="delete_account" />}
            render={() => <LazyDeleteAccount />}
          />
        }
      />
      <Route
        path="export/:exportId"
        element={
          <WithMainLayout
            title={<Translate value="data_reports" />}
            render={(params) => (
              <LazyDataReports {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="data/reports"
        element={
          <WithMainLayout
            title={<Translate value="data_reports" />}
            render={(params) => (
              <LazyDataReports {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="shift-settings"
        element={
          <PermissionRoute screen="shiftSettings">
            <WithMainLayout
              title={<Translate value="shift_schedule" />}
              render={(params) => (
                <ShiftSettingsPage {...({ match: { params } } as any)} />
              )}
            />
          </PermissionRoute>
        }
      />
      <Route
        path="data-analysis/*"
        element={
          <WithMainLayout
            title={<Translate value="data-analysis" />}
            render={(params) => (
              <DataAnalysisPage {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="ui-config/*"
        element={
          <WithMainLayout
            title={<Translate value="ui_config" />}
            render={(params) => (
              <UIConfigScreen
                {...({ match: { params } } as any)}
                key={location.key}
              />
            )}
          />
        }
      />
      <Route
        path="production-settings/*"
        element={
          <PermissionRoute screen="productionSettings">
            <WithMainLayout
              title={<Translate value="production_settings" />}
              render={(params) => (
                <LazyProductionSettingsV2
                  {...({ match: { params } } as any)}
                  key={location.key}
                />
              )}
            />
          </PermissionRoute>
        }
      />
      {import.meta.env.DEV && (
        <Route
          path="settings-v2/*"
          element={
            <WithMainLayout
              title={<Translate value="settings" />}
              render={(params) => (
                <LazySettingsV2 {...({ match: { params } } as any)} />
              )}
            />
          }
        />
      )}

      <Route
        path="realtime-settings/*"
        element={
          <WithMainLayout
            title={<Translate value="realtime_settings" />}
            render={(params) =>
              realtimeV2 ? (
                <LazyRealtimeSettings {...({ match: { params } } as any)} />
              ) : (
                GoToDefaultRoute
              )
            }
          />
        }
      />

      <Route path="*" element={GoToDefaultRoute} />
    </Routes>
  );
}

export default MainLayoutRoute;

const WithMainLayout = (props: {
  render: (params: any) => React.ReactElement;
  title?: any;
  className?: string;
  headerTitleClassName?: string;
  headerTitleTextClassName?: string;
}) => {
  const params = useParams();
  // Rendered only after MainLayoutRoute has a selected company, so the config
  // request carries x-company-id.
  useCanaryChannel();

  const {
    data: companies,
    selectedCompany,
    isLoading: isCompaniesLoading
  } = useUserCompanies();
  const userProfile = useUserProfile({ enabled: !!companies });
  const machinesStore = useSelector((store: any) => store.machines);
  const isConfigLoading = useSelector(configIsLoadingSelector);
  const machineGroups = useSelector(getUserMachineGroupsSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    // after we have the user company in storage, we can dispatch these calls, as the company id is embedded in each
    // request header
    if (selectedCompany) {
      // even though we are consuming the user profile and companies from the hooks, we need to dispatch these
      // as they include important side effects and other components consume state from the store
      dispatch(getUserCompanies());
      dispatch(getUserProfileData());
      dispatch(getMachines({}));
      dispatch(
        loadMachinesSortingFilter(
          String(selectedCompany.companyId),
          EMachineListsVariants.Default
        )
      );
      dispatch(
        loadMachinesSortingFilter(
          String(selectedCompany.companyId),
          EMachineListsVariants.Shift
        )
      );
    }
  }, [dispatch, selectedCompany]);

  const { isAccessSuspended } = useFeatureFlags();

  if (
    userProfile.isLoading ||
    isCompaniesLoading ||
    machinesStore.isMachinesListLoading ||
    isConfigLoading ||
    machineGroups.isLoading
  ) {
    return (
      <div>
        <Spinner id="main-loader" />
      </div>
    );
  }

  if (!machineGroups.allowAllMachines && machineGroups.list.length === 0) {
    return <NoMachineGroupsPage />;
  }

  if (isAccessSuspended) {
    return <AccountSuspendedPage />;
  }

  return (
    <AppLayout
      title={props.title}
      mainContentClassName={cn(props.className)}
      headerTitleClassName={props.headerTitleClassName}
      headerTitleTextClassName={props.headerTitleTextClassName}
    >
      <Suspense fallback={<SpinnerComponent id="fallback-loader" />}>
        {props.render(params)}
      </Suspense>
    </AppLayout>
  );
};

const GoToDefaultRoute = <Navigate to="/realtime" />;
